import { Slider as MuiSlider } from '@mui/material'

export type SliderOption<T = string> = {
  value: T
  label: string
}

type SliderProps<T = string> = {
  options: SliderOption<T>[]
  value: T
  onChange: (value: T) => void
  disabled?: boolean
  ariaLabel?: string
}

export default function Slider<T = string>({
  options,
  value,
  onChange,
  disabled = false,
  ariaLabel = 'Slider',
}: SliderProps<T>) {
  const marks = options.map((opt, i) => ({ value: i, label: opt.label }))
  const valueIndex = options.findIndex((opt) => opt.value === value)
  const sliderValue = valueIndex >= 0 ? valueIndex : 0

  const handleChange = (_event: Event, newValue: number | number[], _activeThumb?: number) => {
    const index = typeof newValue === 'number' ? newValue : (newValue[0] ?? 0)
    const option = options[index]
    if (option) {
      onChange(option.value)
    }
  }

  const valueLabelFormat = (index: number) => {
    const option = options[index]
    return option?.label ?? ''
  }

  return (
    <MuiSlider
      aria-label={ariaLabel}
      value={sliderValue}
      onChange={handleChange}
      marks={marks}
      min={0}
      max={options.length - 1}
      step={null}
      size="small"
      disabled={disabled}
      valueLabelDisplay="auto"
      valueLabelFormat={valueLabelFormat}
      sx={{
        color: '#66717C', // midfi grey
        maxWidth: 220,
        margin: '0 auto',
        marginBottom: 0,
        '& .MuiSlider-thumb': {
          width: '9px',
          height: '9px',
          '&:hover, &.Mui-focusVisible': {
            boxShadow: 'none',
          },
        },
        '& .MuiSlider-mark': {
          width: 2,
          height: 8,
          borderRadius: 0,
          backgroundColor: 'currentColor',
          opacity: 0.5,
        },
        '& .MuiSlider-markLabel': {
          fontSize: 12,
          maxWidth: 36,
          whiteSpace: 'normal',
          marginTop: -1,
          color: '#66717C',
        },
        '& .MuiSlider-root': {
          height: '5px',
        },
      }}
    />
  )
}
